// src/components/RankingList.tsx
import React from 'react';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  TouchSensor, 
  useSensor, 
  useSensors,
  DragEndEvent 
} from '@dnd-kit/core';
import { 
  SortableContext, 
  sortableKeyboardCoordinates, 
  verticalListSortingStrategy 
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { RankingItem } from './RankingItem';
import { EmptyRankingSlot } from './EmptyRankingSlot';
import { Participant } from '../data/eurovisionData';

interface RankingListProps {
  items: (Participant | null)[];
  onAddToSlot: (index: number) => void;
  onRemove: (index: number) => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
  onShowDetails: (participant: Participant) => void;
}

export function RankingList({ 
  items, 
  onAddToSlot, 
  onRemove, 
  onReorder, 
  onShowDetails 
}: RankingListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Create sortable IDs for each slot (filled or empty)
  const sortableIds = items.map((item, index) => 
    item ? `filled-${item.id}` : `empty-${index}`
  );
  
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      // Extract the indexes from the sortable IDs
      const oldIndex = sortableIds.indexOf(active.id as string);
      const newIndex = sortableIds.indexOf(over.id as string);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    }
  }
  
  return (
    <div className="mb-6">
      <h2 className="text-xl font-semibold mb-3">Your Eurovision 2025 Ranking</h2>
      
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext 
          items={sortableIds} 
          strategy={verticalListSortingStrategy}
        >
          {items.map((item, index) => {
            const sortableId = sortableIds[index];
            
            return item ? (
              <RankingItem 
                key={sortableId}
                id={sortableId}
                participant={item} 
                index={index}
                onRemove={() => onRemove(index)}
                onShowDetails={onShowDetails}
                onReplace={() => onAddToSlot(index)}
              />
            ) : (
              <EmptyRankingSlot 
                key={sortableId}
                id={sortableId}
                index={index} 
                onAddToSlot={() => onAddToSlot(index)} 
              />
            );
          })}
        </SortableContext>
      </DndContext>
    </div>
  );
}